import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { Link, useSearchParams } from 'react-router-dom';
import ModalOpciones, { ModalEligeCombo } from './ModalOpciones';
import { db } from '../../firebase/config';
import {
  ShoppingCart, Plus, Minus, X, ChefHat, Gift, Search,
  Phone, User, MapPin, MessageSquare, CheckCircle2,
  ArrowLeft, Trash2, ChevronRight, Clock, CalendarClock, Check,
  Banknote, Building2, CreditCard, Copy,
} from 'lucide-react';
import {
  tieneOpciones, precioConOpciones, nombreConOpciones, idLineaCarrito,
} from '../../utils/opcionesHelper';
import { ordenarCategorias, ordenarCatalogo } from '../../utils/ordenCategorias';
import { normalizarTel } from '../../utils/fidelizacion';
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix icon url for Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Métodos de pago del menú público (sin marcas: el cobro lo hace el local).
const METODOS_PAGO_WEB = [
  { id: 'efectivo',      icon: Banknote,   nombre: 'Efectivo',      desc: 'Pagas en efectivo al recibir tu pedido.' },
  { id: 'transferencia', icon: Building2,  nombre: 'Transferencia', desc: 'Copia los datos, transfiere y envía el comprobante.' },
  { id: 'tarjeta',       icon: CreditCard, nombre: 'Tarjeta',       desc: 'Débito o crédito, se cobra al momento de la entrega.' },
];

function MapSelector({ position, setPosition }) {
  useMapEvents({
    click(e) {
      setPosition({ lat: e.latlng.lat, lng: e.latlng.lng });
    },
  });
  return position ? <Marker position={position} /> : null;
}

function ChangeView({ center, zoom }) {
  const map = useMap();
  React.useEffect(() => {
    if (center) map.flyTo(center, zoom, { duration: 1.5 });
  }, [center, map, zoom]);
  return null;
}

// El peso chileno no usa decimales: sin maximumFractionDigits, un precio
// calculado por margen (4931.68) se mostraba "4.931,68" al cliente.
const formatCLP = (n) =>
  new Intl.NumberFormat('es-CL', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n || 0);

// ─── Datos de transferencia (se editan en Página Web → Transferencia) ─────────
const CampoCopiable = ({ label, valor, destacado }) => {
  const [copiado, setCopiado] = useState(false);
  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(String(valor));
      setCopiado(true);
      setTimeout(() => setCopiado(false), 1800);
    } catch (_) {
      // Navegador sin permiso de portapapeles: el dato igual está visible
    }
  };
  return (
    <button type="button" onClick={copiar}
      className="w-full flex items-center gap-3 px-3.5 py-2.5 rounded-xl bg-white border border-slate-200 hover:border-brand-300 transition-colors text-left group">
      <div className="min-w-0 flex-1">
        <p className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">{label}</p>
        <p className={`truncate ${destacado
          ? 'text-base font-black text-slate-900 font-mono tracking-tight'
          : 'text-sm font-semibold text-slate-700'}`}>{valor}</p>
      </div>
      <span className={`shrink-0 flex items-center gap-1 text-xs font-bold transition-colors
        ${copiado ? 'text-green-600' : 'text-slate-400 group-hover:text-brand-600'}`}>
        {copiado ? <><Check size={14} /> Copiado</> : <><Copy size={14} /> Copiar</>}
      </span>
    </button>
  );
};

const DatosTransferencia = ({ datos, monto }) => {
  const campos = [
    ['Banco',          datos?.banco],
    ['Tipo de cuenta', datos?.tipoCuenta],
    ['N° de cuenta',   datos?.numero, true],
    ['Titular',        datos?.titular],
    ['RUT',            datos?.rut],
    ['Email',          datos?.email],
  ].filter(([, v]) => (v || '').toString().trim());

  if (!campos.length) {
    return (
      <div className="rounded-2xl bg-slate-50 border border-slate-200 p-4 text-sm text-slate-500">
        Te enviaremos los datos de la cuenta al confirmar tu pedido.
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50/70 p-3 space-y-2">
      <div className="flex items-center justify-between px-1">
        <p className="text-sm font-bold text-slate-800">Datos para transferir</p>
        {monto > 0 && <span className="text-sm font-black text-brand-600">{formatCLP(monto)}</span>}
      </div>
      {campos.map(([label, valor, destacado]) => (
        <CampoCopiable key={label} label={label} valor={valor} destacado={destacado} />
      ))}
      <p className="text-[11px] text-slate-400 px-1 leading-relaxed">
        Toca cualquier dato para copiarlo. Guarda el comprobante y muéstralo al recibir tu pedido.
      </p>
    </div>
  );
};


// ─── Página pública del restaurante ──────────────────────────────────────────
const BrandIcons = {
  whatsapp: <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z"/></svg>,
  instagram: <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm3.98-10.169a1.44 1.44 0 1 1-2.88 0 1.44 1.44 0 0 1 2.88 0z"/></svg>,
  facebook: <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5"><path d="M13.397 20.997v-8.196h2.765l.411-3.209h-3.176V7.548c0-.926.258-1.56 1.587-1.56h1.684V3.127A22.336 22.336 0 0 0 14.201 3c-2.444 0-4.122 1.492-4.122 4.231v2.355H7.332v3.209h2.753v8.202h3.312z"/></svg>,
  tiktok: <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5"><path d="M12.525.02L12.5 0h-3.08v19.49a3.26 3.26 0 1 1-3.26-3.26c.55 0 1.07.14 1.53.38v-3.32a6.56 6.56 0 0 0-1.53-.19 6.52 6.52 0 1 0 6.52 6.52V6.62c2.18 1.93 5.03 3.1 8.16 3.1V6.43a9.7 9.7 0 0 1-5.74-2c-1.67-1.35-2.73-3.4-2.73-5.69h-2.12z"/></svg>,
  google: <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5"><path d="M12.48 10.92v3.28h7.84c-.24 1.84-.853 3.187-1.787 4.133-1.147 1.147-2.933 2.4-6.053 2.4-4.827 0-8.6-3.893-8.6-8.72s3.773-8.72 8.6-8.72c2.6 0 4.507 1.027 5.907 2.347l2.307-2.307C18.747 1.44 16.133 0 12.48 0 5.867 0 .307 5.387.307 12s5.56 12 12.173 12c3.573 0 6.267-1.173 8.373-3.36 2.16-2.16 2.84-5.213 2.84-7.667 0-.76-.053-1.467-.173-2.053H12.48z"/></svg>,
  tripadvisor: <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5"><path d="M22.58 10.3c-.536-1.581-1.393-3.033-2.529-4.285a11.397 11.397 0 00-3.593-2.61A11.751 11.751 0 0012 2.392a11.75 11.75 0 00-4.457 1.013A11.396 11.396 0 003.95 6.015a11.516 11.516 0 00-2.529 4.285c-.417 1.258-.621 2.593-.604 3.931a1.267 1.267 0 00.323.864 1.254 1.254 0 00.864.407h6.732a3.861 3.861 0 106.527 0H22c.328 0 .641-.129.873-.359.231-.231.36-.544.36-.871 0-1.332-.208-2.66-.653-3.972zM7.558 18.064c-1.42.001-2.782-.562-3.786-1.564-1.004-1.003-1.568-2.365-1.57-3.785.002-1.42.566-2.783 1.57-3.787 1.004-1.002 2.365-1.565 3.786-1.564 1.42-.001 2.782.562 3.785 1.564 1.004 1.004 1.567 2.367 1.57 3.787-.003 1.42-.566 2.782-1.57 3.785-1.003 1.002-2.365 1.565-3.785 1.564zm8.884 0c-1.42.001-2.782-.562-3.786-1.564-1.003-1.003-1.567-2.365-1.57-3.785.003-1.42.567-2.783 1.57-3.787 1.004-1.002 2.365-1.565 3.786-1.564 1.42-.001 2.782.562 3.785 1.564 1.004 1.004 1.568 2.367 1.571 3.787-.003 1.42-.567 2.782-1.571 3.785-1.003 1.002-2.365 1.565-3.785 1.564zm-5.61-4.285a2.292 2.292 0 11-2.34-3.141 2.292 2.292 0 012.34 3.141zm8.884 0a2.292 2.292 0 11-2.341-3.141 2.292 2.292 0 012.34 3.141z"/></svg>,
  youtube: <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>,
  web: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5"><circle cx="12" cy="12" r="10"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/><path d="M2 12h20"/></svg>
};

const REDES_META = [
  { id: 'whatsapp',    icon: BrandIcons.whatsapp,    label: 'WhatsApp',    buildUrl: (v) => `https://wa.me/${v.replace(/\D/g,'')}` },
  { id: 'instagram',   icon: BrandIcons.instagram,   label: 'Instagram',   buildUrl: (v) => v },
  { id: 'facebook',    icon: BrandIcons.facebook,    label: 'Facebook',    buildUrl: (v) => v },
  { id: 'tiktok',      icon: BrandIcons.tiktok,      label: 'TikTok',      buildUrl: (v) => v },
  { id: 'google',      icon: BrandIcons.google,      label: 'Google',      buildUrl: (v) => v },
  { id: 'tripadvisor', icon: BrandIcons.tripadvisor, label: 'TripAdvisor', buildUrl: (v) => v },
  { id: 'youtube',     icon: BrandIcons.youtube,     label: 'YouTube',     buildUrl: (v) => v },
  { id: 'web',         icon: BrandIcons.web,         label: 'Sitio web',   buildUrl: (v) => v },
];

const DIA_KEYS = ['dom', 'lun', 'mar', 'mie', 'jue', 'vie', 'sab'];

// ¿Ese "HH:MM" cae dentro del horario del día? (cierra < abre = cierre pasada la medianoche)
const dentroDeHorario = (dia, hhmm) => dia.cierra > dia.abre
  ? hhmm >= dia.abre && hhmm < dia.cierra
  : hhmm >= dia.abre || hhmm < dia.cierra;

// <input type="datetime-local"> trabaja en hora local; toISOString() es UTC y en
// Chile adelantaría el mínimo 3-4 horas.
const aInputLocal = (d) =>
  new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);

// soloVer: carta del mesón (QR de caja). Misma página, sin carrito: se mira y se pide en caja.
export default function MenuPublico({ soloVer = false }) {
  const [params] = useSearchParams();
  const [noAutorizado, setNoAutorizado] = useState(false);
  const [restaurante, setRestaurante] = useState({ nombre: 'RestManager CL', descripcion: '' });
  const [redes, setRedes] = useState({});
  const [paginaWeb, setPaginaWeb] = useState({ horarios: null, banners: [], ordenCategorias: [] });
  const [recetas, setRecetas]         = useState([]);
  const [productosDirect, setProductosDirect] = useState([]);
  const [combos, setCombos]           = useState([]);
  const [cargando, setCargando]       = useState(true);

  const [busqueda, setBusqueda]       = useState('');
  const [catFiltro, setCatFiltro]     = useState('');

  const [carrito, setCarrito]         = useState([]);
  const [carritoAbierto, setCarritoAbierto] = useState(false);
  const [comboEligiendo, setComboEligiendo] = useState(null);
  const [itemOpciones, setItemOpciones]     = useState(null);

  // paso: 'menu' | 'checkout' | 'confirmado'
  const [paso, setPaso]               = useState('menu');
  const [pedidoNumero, setPedidoNumero] = useState(null);
  const [pedidoTotal, setPedidoTotal] = useState(0);
  const [pedidoId, setPedidoId]       = useState(null);
  // Último pedido guardado en el navegador: permite volver a ver el estado
  // aunque el cliente cierre la página (el id del doc es el "ticket").
  const [pedidoGuardado] = useState(() => {
    try { return JSON.parse(localStorage.getItem('ultimoPedidoWeb') || 'null'); } catch { return null; }
  });
  const [enviando, setEnviando]       = useState(false);

  // ── Formulario y Estado de Pedido ──────────────────────────────────────────
  const [formPedido, setFormPedido] = useState({
    nombre: '', telefono: '', tipo: 'llevar', mesa: '', direccion: '', notas: '',
    esProgramado: false, fechaProgramada: '', metodoPago: 'efectivo',
    ubicacion: null,
  });

  // ── Mapa y Geocodificación ─────────────────────────────────────────────────
  const [mapCenter, setMapCenter] = useState([-38.7359, -72.5904]); // Temuco por defecto

  useEffect(() => {
    if (formPedido.tipo !== 'delivery' || formPedido.direccion.trim().length < 5) return;
    const timer = setTimeout(async () => {
      try {
        const q = encodeURIComponent(`${formPedido.direccion}, Temuco, Chile`);
        const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${q}&limit=1`);
        const data = await res.json();
        if (data && data.length > 0) {
          const lat = parseFloat(data[0].lat);
          const lon = parseFloat(data[0].lon);
          setMapCenter([lat, lon]);
          setFormPedido(p => ({ ...p, ubicacion: { lat, lng: lon } }));
        }
      } catch (err) {
        // Error silencioso si falla la geocodificación
      }
    }, 1500); // 1.5s de debounce
    return () => clearTimeout(timer);
  }, [formPedido.direccion, formPedido.tipo]);

  // ── Carga datos vía endpoints PÚBLICOS sanitizados ──────────────────────
  // (No accedemos a Firestore directo para no exponer costos/stock real)
  const PUBLIC_API = (import.meta.env.VITE_BACKEND_URL ||
    'https://southamerica-east1-cocina-19123.cloudfunctions.net/api');

  useEffect(() => {
    fetch(`${PUBLIC_API}/api/public/config`)
      .then(r => r.json())
      .then(d => { if (d?.ok && d.config) setRestaurante(d.config); })
      .catch(() => {});
    // Redes sociales se mantienen en Firestore por ahora (datos no sensibles).
    getDoc(doc(db, 'config', 'redes_sociales'))
      .then(d => { if (d.exists()) setRedes(d.data()); })
      .catch(() => {});
    // Horarios y banners (datos públicos, editables desde el módulo Página Web)
    getDoc(doc(db, 'config', 'pagina_web'))
      .then(d => { if (d.exists()) setPaginaWeb(prev => ({ ...prev, ...d.data() })); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    let cancelado = false;
    const cargar = async () => {
      try {
        // canal=web: el menú para llevar/delivery · canal=meson: carta del mesón (exige el código del QR)
        const resp = await fetch(`${PUBLIC_API}/api/public/menu?` + (soloVer
          ? `canal=meson&t=${encodeURIComponent(params.get('t') || '')}`
          : 'canal=web'));
        const data = await resp.json();
        if (cancelado) return;
        setNoAutorizado(!data?.ok);
        if (!data?.ok) return;
        setRecetas(data.recetas || []);
        setProductosDirect(data.productos || []);
        setCombos(data.combos || []);
      } catch (err) {
        console.error('Error cargando menú:', err);
      } finally {
        if (!cancelado) setCargando(false);
      }
    };
    cargar();
    // Refresh cada 60s para tener stock/disponibilidad actualizado
    const interval = setInterval(cargar, 60000);
    return () => { cancelado = true; clearInterval(interval); };
  }, []);

  // ── Catálogo ───────────────────────────────────────────────────────────────
  const catalogo = useMemo(() => ordenarCatalogo([
    ...recetas.map(r => ({ ...r, _tipo: 'receta', precioVenta: r.precioVenta ?? r.precioSugeridoVenta ?? 0 })),
    ...productosDirect.map(p => ({ ...p, _tipo: 'producto', categoria: p.categoria || 'productos', precioVenta: p.precioVenta || p.precioVentaDirecto || 0 })),
    ...combos.map(c => ({ ...c, _tipo: 'combo', categoria: 'combos', precioVenta: c.precio || 0 })),
  ], paginaWeb.ordenCategorias), [recetas, productosDirect, combos, paginaWeb.ordenCategorias]);

  // Ordenadas según el orden definido en el módulo admin "Página Web"
  const categorias = useMemo(
    () => ordenarCategorias([...new Set(catalogo.map(i => i.categoria))].filter(Boolean), paginaWeb.ordenCategorias),
    [catalogo, paginaWeb.ordenCategorias]);

  // Estado abierto/cerrado según horarios configurados
  const estadoApertura = useMemo(() => {
    const h = paginaWeb.horarios;
    if (!h) return null;
    const now = new Date();
    const hoy = h[DIA_KEYS[now.getDay()]];
    if (!hoy || !hoy.abierto) return { abierto: false, texto: 'Cerrado hoy' };
    const hhmm = now.toTimeString().slice(0, 5);
    return dentroDeHorario(hoy, hhmm)
      ? { abierto: true, texto: `Abierto · Hasta las ${hoy.cierra}` }
      : { abierto: false, texto: `Cerrado · Hoy atendemos de ${hoy.abre} a ${hoy.cierra}` };
  }, [paginaWeb.horarios]);

  // Avisa (sin bloquear) si la hora elegida cae fuera del horario de atención.
  const avisoHorario = useMemo(() => {
    const { esProgramado, fechaProgramada } = formPedido;
    if (!esProgramado || !fechaProgramada || !paginaWeb.horarios) return '';
    const d = new Date(fechaProgramada);
    const dia = paginaWeb.horarios[DIA_KEYS[d.getDay()]];
    if (!dia) return '';
    if (!dia.abierto) return 'Ese día no atendemos. Elige otra fecha.';
    return dentroDeHorario(dia, aInputLocal(d).slice(11, 16))
      ? ''
      : `Ese día atendemos de ${dia.abre} a ${dia.cierra}. Revisa la hora.`;
  }, [formPedido.esProgramado, formPedido.fechaProgramada, paginaWeb.horarios]);

  const bannersActivos = useMemo(
    () => (paginaWeb.banners || []).filter(b => b.activo !== false),
    [paginaWeb.banners]);

  // Ya viene ordenado por categoría (orden del módulo "Página Web"): sólo filtramos
  const catalogoFiltrado = useMemo(() => catalogo.filter(i => {
    const b = i.nombre.toLowerCase().includes(busqueda.toLowerCase());
    const c = !catFiltro || i.categoria === catFiltro;
    return b && c;
  }), [catalogo, busqueda, catFiltro]);

  const totalCarrito    = useMemo(() => carrito.reduce((s, i) => s + i.subtotal, 0), [carrito]);
  const cantidadCarrito = useMemo(() => carrito.reduce((s, i) => s + i.cantidad, 0), [carrito]);

  // ── Código promocional ─────────────────────────────────────────────────────
  const [codigoInput, setCodigoInput] = useState('');
  const [promo, setPromo] = useState(null); // { codigo, tipo, valor }
  const [promoError, setPromoError] = useState('');
  const [validandoPromo, setValidandoPromo] = useState(false);

  const descuentoPromo = useMemo(() => {
    if (!promo) return 0;
    const d = promo.tipo === 'porcentaje'
      ? Math.round(totalCarrito * promo.valor / 100)
      : promo.valor;
    return Math.min(d, totalCarrito);
  }, [promo, totalCarrito]);
  const totalAPagar = totalCarrito - descuentoPromo;

  const aplicarCodigo = async () => {
    const code = codigoInput.trim().toUpperCase().replace(/\s+/g, '');
    if (!code) return;
    setValidandoPromo(true);
    setPromoError('');
    try {
      // Validación vía endpoint (con límite por IP) en vez de leer Firestore
      // directo: así no se pueden probar códigos sin freno. El descuento
      // definitivo lo recalcula el servidor al crear el pedido.
      const r = await fetch(`${PUBLIC_API}/api/public/promo/${encodeURIComponent(code)}`);
      const d = await r.json().catch(() => null);
      if (!r.ok || !d?.ok) {
        setPromo(null);
        setPromoError(d?.error || 'Código inválido o vencido');
      } else {
        setPromo(d.promo);
      }
    } catch {
      setPromoError('No se pudo validar el código, intenta de nuevo');
    } finally {
      setValidandoPromo(false);
    }
  };

  const quitarCodigo = () => { setPromo(null); setCodigoInput(''); setPromoError(''); };

  const sugerenciasUpsell = useMemo(() => {
    const enCarrito = carrito.map(c => c.carritoId);
    // Sugerir bebidas o postres como Cross-Selling Inteligente
    const vendible = i => !enCarrito.includes(i.id) && i.precioVenta > 0 && i.disponible !== false;
    const disponibles = catalogo.filter(i => vendible(i) && (i.categoria?.toLowerCase() === 'bebidas' || i.categoria?.toLowerCase() === 'postres'));
    if (disponibles.length > 0) return disponibles.sort(() => 0.5 - Math.random()).slice(0, 3);
    const otros = catalogoFiltrado.filter(vendible);
    return otros.sort(() => 0.5 - Math.random()).slice(0, 3);
  }, [catalogo, catalogoFiltrado, carrito]);

  // ── Carrito helpers ────────────────────────────────────────────────────────
  const agregarItem = useCallback((item) => {
    if (item.disponible === false) return;   // agotado: no entra al carrito por ningún camino
    if (soloVer) { setItemOpciones(item); return; }   // mesón: abre la ficha, no agrega
    if (item._tipo === 'combo' && item.componentes?.some(c => c.tipo === 'elige')) {
      setComboEligiendo(item);
      return;
    }
    // Tamaños o agregados: el cliente elige antes de sumar al carrito
    if (tieneOpciones(item)) {
      setItemOpciones(item);
      return;
    }
    setCarrito(prev => {
      const cid = item.carritoId ?? item.id;
      const idx = prev.findIndex(i => i.carritoId === cid);
      if (idx >= 0) {
        const n = [...prev];
        n[idx] = { ...n[idx], cantidad: n[idx].cantidad + 1, subtotal: (n[idx].cantidad + 1) * n[idx].precioUnitario };
        return n;
      }
      return [...prev, {
        carritoId: cid,
        nombre: item.nombre,
        _tipo: item._tipo || 'receta',
        cantidad: 1,
        precioUnitario: item.precioVenta,
        subtotal: item.precioVenta,
        nota: '',
      }];
    });
  }, []);

  const incrementarItem = useCallback((carritoId) => {
    setCarrito(prev => {
      const idx = prev.findIndex(i => i.carritoId === carritoId);
      if (idx < 0) return prev;
      const n = [...prev];
      n[idx] = { ...n[idx], cantidad: n[idx].cantidad + 1, subtotal: (n[idx].cantidad + 1) * n[idx].precioUnitario };
      return n;
    });
  }, []);

  const confirmarOpciones = useCallback(({ variante, agregados, precioUnitario }) => {
    if (!itemOpciones) return;
    const base = itemOpciones;
    const nombreCompleto = nombreConOpciones(base.nombre, variante, agregados);
    const carritoId = idLineaCarrito(base.id, variante, agregados);

    setCarrito(prev => {
      const idx = prev.findIndex(i => i.carritoId === carritoId);
      if (idx >= 0) {
        const n = [...prev];
        n[idx] = { ...n[idx], cantidad: n[idx].cantidad + 1, subtotal: (n[idx].cantidad + 1) * n[idx].precioUnitario };
        return n;
      }
      return [...prev, {
        carritoId,
        baseId: base.id,
        nombre: nombreCompleto,
        _tipo: base._tipo || 'receta',
        variante: variante || null,
        agregados,
        cantidad: 1,
        precioUnitario,
        subtotal: precioUnitario,
        nota: '',
      }];
    });
    setItemOpciones(null);
  }, [itemOpciones]);

  const confirmarComboElige = useCallback((selecciones) => {
    if (!comboEligiendo) return;
    const combo = comboEligiendo;
    const nombresElegidos = Object.values(selecciones).filter(Boolean).map(s => s.nombre).join(' + ');
    const nombreCompleto = nombresElegidos ? `${combo.nombre} (${nombresElegidos})` : combo.nombre;
    const selHash = Object.entries(selecciones).map(([k, v]) => `${k}:${v?.id}`).join('|');
    const carritoId = `combo_${combo.id}_${selHash}`;
    setCarrito(prev => {
      const idx = prev.findIndex(i => i.carritoId === carritoId);
      if (idx >= 0) {
        const n = [...prev];
        n[idx] = { ...n[idx], cantidad: n[idx].cantidad + 1, subtotal: (n[idx].cantidad + 1) * n[idx].precioUnitario };
        return n;
      }
      return [...prev, {
        carritoId, nombre: nombreCompleto, _tipo: 'combo', comboId: combo.id,
        // Lo elegido viaja al servidor para que cocina vea qué pizza y qué
        // bebida pidió el cliente, no solo "Promo familiar".
        selecciones: Object.values(selecciones).filter(Boolean)
          .map(s => ({ id: s.id, nombre: s.nombre })),
        cantidad: 1, precioUnitario: combo.precio || 0, subtotal: combo.precio || 0, nota: '',
      }];
    });
    setComboEligiendo(null);
  }, [comboEligiendo]);

  const restarItem = useCallback((carritoId) => {
    setCarrito(prev => {
      const idx = prev.findIndex(i => i.carritoId === carritoId);
      if (idx < 0) return prev;
      const item = prev[idx];
      if (item.cantidad <= 1) return prev.filter(i => i.carritoId !== carritoId);
      const n = [...prev];
      n[idx] = { ...item, cantidad: item.cantidad - 1, subtotal: (item.cantidad - 1) * item.precioUnitario };
      return n;
    });
  }, []);

  const quitarItem = useCallback((carritoId) => {
    setCarrito(prev => prev.filter(i => i.carritoId !== carritoId));
  }, []);

  // Suma también las líneas con tamaños/agregados (llevan id compuesto)
  const cantDeItem   = (id) => carrito
    .filter(i => i.carritoId === id || i.baseId === id)
    .reduce((s, i) => s + i.cantidad, 0);
  const cantDeCombo  = (id) => carrito.filter(i => i.comboId === id).reduce((s, i) => s + i.cantidad, 0);

  // Teléfono obligatorio: para avisar que el pedido está listo o confirmar datos
  const telValido = /^\d{8,15}$/.test(normalizarTel(formPedido.telefono));

  // ── Enviar pedido ──────────────────────────────────────────────────────────
  const enviarPedido = async () => {
    if (!formPedido.nombre.trim() || !telValido || carrito.length === 0) return;
    if (formPedido.esProgramado && !formPedido.fechaProgramada) return;

    // Validar disponibilidad antes de crear la comanda
    const noDisponible = carrito.find(it => {
      if (it._tipo !== 'producto') return false;
      const prod = catalogo.find(c => c.id === it.carritoId || c.id === it.id);
      if (!prod) return false;
      if (prod.disponible === false) return true;
      // Solo bloqueamos si hay stock conocido < cantidad pedida
      return !prod.stockInfinito && typeof prod.cantidadActual === 'number' && prod.cantidadActual < it.cantidad;
    });
    if (noDisponible) {
      alert(`Lo sentimos, "${noDisponible.nombre}" ya no está disponible o no hay stock suficiente. Por favor ajusta tu pedido.`);
      setEnviando(false);
      return;
    }

    setEnviando(true);
    try {
      // El pedido lo crea el servidor: acá solo se manda QUÉ se pide.
      // Precios, descuento y total se recalculan contra la base en
      // POST /api/public/pedido — el navegador ya no puede fijar el monto.
      const resp = await fetch(`${PUBLIC_API}/api/public/pedido`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          canal: 'web_propia',
          cliente: {
            nombre: formPedido.nombre.trim(),
            telefono: formPedido.telefono.trim(),
            direccion: formPedido.tipo === 'delivery' ? formPedido.direccion.trim() : '',
            ubicacion: formPedido.tipo === 'delivery' ? formPedido.ubicacion : null,
          },
          tipoPedido: formPedido.tipo,
          metodoPago: formPedido.metodoPago || 'efectivo',
          notas: formPedido.notas.trim(),
          esProgramado: formPedido.esProgramado,
          fechaProgramada: formPedido.esProgramado ? formPedido.fechaProgramada : null,
          codigoPromo: promo?.codigo || null,
          items: carrito.map(i => ({
            id: i.baseId || i.comboId || i.carritoId,
            tipo: i._tipo,
            cantidad: i.cantidad,
            varianteId: i.variante?.id || null,
            agregadoIds: (i.agregados || []).map(a => a.id),
            selecciones: i.selecciones || [],   // componentes elegidos de un combo
            nota: i.nota || '',
            nombre: i.nombre,   // solo para el mensaje de error
          })),
        }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok || !data.ok) {
        alert(data.error || 'No pudimos registrar el pedido. Intenta nuevamente.');
        setEnviando(false);
        return;
      }

      const { id, numero, total } = data.pedido;
      setPedidoId(id);
      try {
        localStorage.setItem('ultimoPedidoWeb', JSON.stringify({ id, numero, fecha: Date.now() }));
      } catch (_) { /* modo incógnito sin storage: el link de esta pantalla igual funciona */ }

      setPedidoNumero(numero);
      setPedidoTotal(total);
      setCarrito([]);
      quitarCodigo();
      setCarritoAbierto(false);
      setPaso('confirmado');
    } catch (err) {
      alert('Error al enviar pedido: ' + err.message);
    } finally {
      setEnviando(false);
    }
  };

  const reiniciar = () => {
    setPaso('menu');
    setFormPedido({ nombre: '', telefono: '', tipo: 'llevar', mesa: '', direccion: '', notas: '', esProgramado: false, fechaProgramada: '', metodoPago: 'efectivo', ubicacion: null });
    setCarrito([]);
    quitarCodigo();
  };

  // ══════════════════════════════════════════════════════════════════════════
  // ── Confirmación ──────────────────────────────────────────────────────────
  if (paso === 'confirmado') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-3xl shadow-xl p-8 max-w-sm w-full text-center space-y-5">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto">
            <CheckCircle2 size={44} className="text-green-500" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              {formPedido.esProgramado ? '¡Reserva confirmada!' : '¡Pedido enviado!'}
            </h2>
            <p className="text-gray-500 text-sm mt-1">
              {formPedido.esProgramado ? 'Tu pedido está programado' : 'Tu pedido ya está en cocina'}
            </p>
          </div>
          <div className="bg-brand-50 border border-brand-200 rounded-2xl p-5">
            <p className="text-xs text-gray-500 uppercase font-semibold mb-1">Número de pedido</p>
            <p className="text-4xl font-black text-brand-600">#{pedidoNumero}</p>
          </div>
          {formPedido.esProgramado && formPedido.fechaProgramada ? (
            <div className="flex items-center gap-2 bg-indigo-50 border border-indigo-200 rounded-xl p-3 text-sm text-indigo-700">
              <CalendarClock size={16} className="shrink-0" />
              <span>Programado para <strong>{new Date(formPedido.fechaProgramada).toLocaleString('es-CL', { weekday: 'short', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</strong></span>
            </div>
          ) : (
            <div className="flex items-center gap-2 bg-gray-50 rounded-xl p-3 text-sm text-gray-600">
              <Clock size={16} className="text-brand-500 shrink-0" />
              <span>El equipo preparará tu pedido a la brevedad</span>
            </div>
          )}
          {formPedido.metodoPago === 'transferencia' && (
            <div className="text-left">
              <DatosTransferencia datos={paginaWeb.transferencia} monto={pedidoTotal} />
            </div>
          )}
          {pedidoId && (
            <Link
              to={`/pedido/${pedidoId}`}
              className="block w-full py-3.5 bg-brand-600 hover:bg-brand-700 text-white font-bold rounded-2xl transition-colors"
            >
              Ver estado de mi pedido
            </Link>
          )}
          <button
            onClick={reiniciar}
            className="w-full py-3.5 border-2 border-gray-200 hover:border-gray-300 text-gray-700 font-bold rounded-2xl transition-colors"
          >
            Hacer otro pedido
          </button>
          <p className="text-[11px] text-gray-400 leading-relaxed">
            Guarda este enlace: puedes seguir tu pedido en cualquier momento desde tu teléfono.
          </p>
        </div>
      </div>
    );
  }

  if (soloVer && noAutorizado) return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center p-6 text-center text-white">
      <div>
        <p className="text-5xl">🍽️</p>
        <h1 className="text-2xl font-bold mt-3">Carta no disponible</h1>
        <p className="text-gray-400 mt-2">Escanea el código QR del mesón para ver la carta.</p>
      </div>
    </div>
  );

  // ══════════════════════════════════════════════════════════════════════════
  // ── Menú principal ────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-[#FDFCF8]">
      {/* Schema.org para SEO y Asistentes IA */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Restaurant",
          "name": restaurante.nombre || "Restaurante",
          "image": restaurante.logoUrl,
          "telephone": restaurante.telefono,
          "address": {
            "@type": "PostalAddress",
            "streetAddress": restaurante.direccion
          },
          "menu": catalogoFiltrado.slice(0, 10).map(item => ({
            "@type": "MenuItem",
            "name": item.nombre,
            "offers": {
              "@type": "Offer",
              "price": item.precioVenta,
              "priceCurrency": "CLP"
            }
          }))
        // `</script>` dentro de cualquier nombre cerraría el bloque y lo que
        // siguiera se ejecutaría como HTML. Escapar el `<` lo impide.
        }).replace(/</g, '\\u003c')
      }} />

      {/* Hero del restaurante */}
      <div className="relative overflow-hidden bg-gray-950 text-white">
        <div className="absolute inset-0">
          {restaurante.heroUrl ? (
            <img src={restaurante.heroUrl} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-brand-800 to-gray-950" />
          )}
          {/* Oscurece abajo y a la izquierda, donde va el texto */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-black/20" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 to-transparent" />
        </div>

        <div className="relative max-w-5xl mx-auto px-4 pt-20 pb-8 md:pt-32 md:pb-12 min-h-[340px] md:min-h-[440px] flex flex-col justify-end">
          <div className="flex items-end gap-4 md:gap-6">
            <div className="w-20 h-20 md:w-28 md:h-28 rounded-2xl overflow-hidden bg-black ring-1 ring-white/20 shadow-2xl shrink-0 flex items-center justify-center text-4xl">
              {restaurante.logoUrl
                ? <img src={restaurante.logoUrl} alt="Logo" className="w-full h-full object-cover" />
                : '🍽️'}
            </div>
            <div className="min-w-0 pb-1">
              <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md ring-1 ring-white/15 text-[11px] md:text-xs px-3 py-1 rounded-full mb-2 md:mb-3">
                <span className={`w-2 h-2 rounded-full ${estadoApertura?.abierto === false ? 'bg-red-500' : 'bg-green-400 animate-pulse'}`} />
                <span className="font-semibold tracking-wider uppercase">
                  {soloVer ? 'Elige con calma y pide en caja' : estadoApertura ? estadoApertura.texto : 'Abierto · Recibiendo pedidos'}
                </span>
              </div>
              {/* text-white explícito: la regla global de h1 lo pinta oscuro */}
              <h1 className="font-display font-semibold text-white text-4xl md:text-6xl leading-none tracking-tight drop-shadow-lg">
                {restaurante.nombre || 'Restaurante'}
              </h1>
            </div>
          </div>

          {restaurante.descripcion && (
            <p className="text-white/80 text-sm md:text-base mt-4 max-w-xl leading-relaxed">
              {restaurante.descripcion}
            </p>
          )}

          <div className="mt-5 md:mt-6 flex flex-wrap items-center gap-x-5 gap-y-3 text-sm text-white/85">
            {restaurante.direccion && (
              <span className="flex items-center gap-1.5">
                <MapPin size={16} className="text-brand-400 shrink-0" />
                {restaurante.direccion}{restaurante.comuna ? `, ${restaurante.comuna}` : ''}
              </span>
            )}
            {restaurante.telefono && (
              <span className="flex items-center gap-1.5">
                <Phone size={16} className="text-brand-400 shrink-0" /> {restaurante.telefono}
              </span>
            )}
            {REDES_META.some(r => redes[r.id]) && (
              <div className="flex gap-2 md:ml-auto">
                {REDES_META.filter(r => redes[r.id]).map(r => (
                  <a
                    key={r.id} href={r.buildUrl(redes[r.id])} target="_blank" rel="noopener noreferrer"
                    className="flex items-center justify-center w-9 h-9 rounded-full bg-white/10 ring-1 ring-white/15 backdrop-blur-md hover:bg-white/25 transition-colors"
                    title={r.label} aria-label={r.label}
                  >
                    {r.icon}
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Pedido reciente en curso (últimas 6 h) */}
      {!soloVer && pedidoGuardado?.id && Date.now() - (pedidoGuardado.fecha || 0) < 6 * 3600e3 && (
        <div className="max-w-5xl mx-auto px-4 pt-4">
          <Link to={`/pedido/${pedidoGuardado.id}`}
            className="flex items-center gap-3 bg-white border-2 border-brand-200 rounded-2xl px-4 py-3 shadow-sm hover:border-brand-400 transition-colors">
            <Clock size={18} className="text-brand-600 shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-black text-gray-900">Tienes un pedido en curso</p>
              <p className="text-xs text-gray-500">Pedido #{pedidoGuardado.numero} · toca para ver su estado</p>
            </div>
            <ChevronRight size={18} className="text-gray-400 shrink-0" />
          </Link>
        </div>
      )}

      {/* Banners de ofertas */}
      {bannersActivos.length > 0 && (
        <div className="max-w-5xl mx-auto px-4 pt-4">
          <div className="flex gap-3 overflow-x-auto snap-x snap-mandatory scrollbar-hide pb-1">
            {bannersActivos.map((b, i) => {
              const Cont = b.enlace ? 'a' : 'div';
              const props = b.enlace ? { href: b.enlace, target: '_blank', rel: 'noopener noreferrer' } : {};
              return (
                <Cont key={b.id || i} {...props}
                  className={`relative shrink-0 snap-center rounded-2xl overflow-hidden shadow-lg border border-gray-100
                    ${bannersActivos.length === 1 ? 'w-full' : 'w-[85%] sm:w-[420px]'} h-36 sm:h-44 block bg-gray-900`}>
                  {b.imagen
                    ? <img src={b.imagen} alt={b.titulo || 'Oferta'} className="absolute inset-0 w-full h-full object-cover" />
                    : <div className="absolute inset-0 bg-gradient-to-br from-[#E64A19] to-[#D32F2F]" />}
                  {(b.titulo || b.texto) && (
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent flex flex-col justify-end p-4">
                      {b.titulo && <p className="text-white font-black text-lg leading-tight drop-shadow">{b.titulo}</p>}
                      {b.texto && <p className="text-gray-200 text-xs mt-0.5 line-clamp-2">{b.texto}</p>}
                    </div>
                  )}
                </Cont>
              );
            })}
          </div>
        </div>
      )}

      {/* Barra búsqueda + categorías */}
      <div className="bg-[#FDFCF8]/90 backdrop-blur-md border-b border-gray-200 sticky top-0 z-40 shadow-sm">
        <div className="max-w-5xl mx-auto px-4 py-3 space-y-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
            <input
              className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-brand-400 bg-gray-50"
              placeholder="Buscar en el menú..."
              value={busqueda}
              onChange={e => setBusqueda(e.target.value)}
            />
          </div>
          <div className="flex gap-2 overflow-x-auto pb-0.5 scrollbar-thin">
            {categorias.map(c => (
              <button key={c} onClick={() => setCatFiltro(catFiltro === c ? '' : c)}
                className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold capitalize transition-colors
                  ${catFiltro === c ? 'bg-brand-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                {c}
              </button>
            ))}
            <button onClick={() => setCatFiltro('')}
              className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors
                ${!catFiltro ? 'bg-brand-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
              Todo el menú
            </button>
          </div>
        </div>
      </div>

      {/* Grid de productos */}
      <div className="max-w-5xl mx-auto px-4 py-5 pb-32">
        {cargando ? (
          <div className="text-center py-20 text-gray-400 space-y-3">
            <div className="inline-block animate-spin rounded-full h-10 w-10 border-4 border-brand-100 border-t-brand-500" />
            <p className="text-sm">Cargando menú...</p>
          </div>
        ) : catalogoFiltrado.length === 0 ? (
          <div className="text-center py-20 text-gray-400 space-y-2">
            <p className="text-5xl">🍽️</p>
            <p className="font-medium">No encontramos resultados</p>
            <p className="text-sm">Intenta con otra búsqueda</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {catalogoFiltrado.map(item => {
              const cant = item._tipo === 'combo' ? cantDeCombo(item.id) : cantDeItem(item.id);
              const esCombo = item._tipo === 'combo';
              const agotado = item.disponible === false;
              const imagen = item.imagen || item.foto || (item.fotos && item.fotos.length > 0 ? item.fotos[0] : null);
              // Platos llenan la foto; productos envasados (botellas, latas) se ven enteros
              const ajusteFoto = item._tipo === 'producto' ? 'object-contain p-2' : 'object-cover';

              const ContenedorImagen = item.enlaceDestino ? 'a' : 'div';
              const propsContenedor = item.enlaceDestino ? {
                href: item.enlaceDestino,
                target: '_blank',
                rel: 'noopener noreferrer',
                onClick: (e) => e.stopPropagation(),
              } : {};

              return (
                <div key={item.id}
                  onClick={() => !agotado && agregarItem(item)}
                  className={`group bg-white rounded-2xl border-2 overflow-hidden flex flex-col transition-all duration-300
                    ${agotado
                      ? 'border-gray-100 shadow-sm cursor-not-allowed'
                      : `cursor-pointer active:scale-95 hover:shadow-xl hover:-translate-y-1 hover:border-[#D32F2F] ${cant > 0
                          ? esCombo ? 'border-[#E64A19] shadow-md' : 'border-[#D32F2F] shadow-md'
                          : 'border-gray-100 shadow-sm'}`}`}>

                  {/* Imagen */}
                  <ContenedorImagen {...propsContenedor}
                    className={`relative aspect-[4/3] shrink-0 bg-white border-b border-gray-50 overflow-hidden block ${agotado ? 'grayscale opacity-50' : ''}`}>
                    {item.fotos && item.fotos.length > 0 ? (
                      <div className="flex w-full h-full overflow-x-auto snap-x snap-mandatory scrollbar-hide">
                        {item.fotos.map((f, i) => (
                          <img key={i} src={f} alt={`${item.nombre} ${i+1}`} className={`w-full h-full ${ajusteFoto} snap-center shrink-0 transition-transform duration-500 group-hover:scale-105`} />
                        ))}
                      </div>
                    ) : imagen ? (
                      <img src={imagen} alt={item.nombre} className={`w-full h-full ${ajusteFoto} transition-transform duration-500 group-hover:scale-105`} />
                    ) : (
                      <div className={`w-full h-full flex items-center justify-center transition-transform duration-500 group-hover:scale-105
                          ${esCombo ? 'bg-gradient-to-br from-amber-50 to-orange-100' : 'bg-slate-50'}`}>
                          {esCombo
                            ? <Gift size={40} className="text-amber-300 transition-transform duration-500 group-hover:rotate-12" />
                            : item._tipo === 'producto'
                              ? <span className="text-4xl transition-transform duration-500 group-hover:-rotate-12">🛒</span>
                              : <ChefHat size={40} className="text-slate-300 transition-transform duration-500 group-hover:-rotate-12" />}
                      </div>
                    )}

                    {agotado && (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className="bg-gray-900/85 text-white text-xs font-black uppercase tracking-wider px-3 py-1.5 rounded-full">
                          Agotado
                        </span>
                      </div>
                    )}

                    {cant > 0 && !agotado && (
                      <div className={`absolute top-2 right-2 w-6 h-6 rounded-full text-xs font-black
                        flex items-center justify-center shadow-lg text-white
                        ${esCombo ? 'bg-amber-500' : 'bg-brand-600'}`}>
                        {cant}
                      </div>
                    )}

                    {esCombo && (
                      <div className="absolute top-2 left-2 flex gap-1">
                        <span className="text-xs bg-amber-500 text-white px-1.5 py-0.5 rounded-full font-semibold">Combo</span>
                        {item.componentes?.some(c => c.tipo === 'elige') && (
                          <span className="text-xs bg-blue-500 text-white px-1.5 py-0.5 rounded-full">A elegir</span>
                        )}
                      </div>
                    )}
                  </ContenedorImagen>

                  {/* Info */}
                  <div className="p-4 flex flex-col justify-between flex-1">
                    <div>
                      <p className="font-display font-semibold text-gray-900 text-lg leading-snug line-clamp-2">{item.nombre}</p>
                      {item.descripcion && (
                        <p className="text-[13px] text-gray-500 mt-1 leading-relaxed line-clamp-2">{item.descripcion}</p>
                      )}
                    </div>
                    <div className="flex items-center justify-between mt-4">
                      <span className={`font-bold text-lg tabular-nums ${esCombo ? 'text-[#E64A19]' : 'text-gray-900'}`}>
                        {item.variantes?.length > 0 && (
                          <span className="text-[10px] font-semibold text-gray-400 mr-1">desde</span>
                        )}
                        {formatCLP(item.precioVenta)}
                      </span>
                      {agotado ? (
                        <span className="text-xs font-bold text-gray-400 uppercase tracking-wide">Agotado</span>
                      ) : soloVer ? null : (
                        <div
                          onClick={e => { e.stopPropagation(); agregarItem(item); }}
                          className={`w-10 h-10 rounded-full flex items-center justify-center text-white shadow-lg
                            transition-all active:scale-90 hover:scale-105
                            ${esCombo ? 'bg-[#E64A19] shadow-orange-500/30' : 'bg-[#D32F2F] shadow-red-500/30'}`}>
                          <Plus size={20} />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Botón carrito flotante */}
      {carrito.length > 0 && (
        <div className="fixed bottom-5 inset-x-4 flex justify-center z-20">
          <button
            onClick={() => setCarritoAbierto(true)}
            className="bg-brand-600 hover:bg-brand-700 text-white px-5 py-4 rounded-2xl shadow-2xl
              flex items-center justify-between gap-3 font-bold text-sm
              transition-all active:scale-95 max-w-sm w-full"
          >
            <div className="flex items-center gap-3">
              <div className="bg-white/25 rounded-xl px-2.5 py-1 font-black">{cantidadCarrito}</div>
              <span>Ver pedido</span>
            </div>
            <span className="font-black">{formatCLP(totalCarrito)}</span>
          </button>
        </div>
      )}

      {/* Drawer del carrito (One-Step Checkout) */}
      {carritoAbierto && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center sm:justify-center">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setCarritoAbierto(false)} />
          <div className="relative bg-[#FDFCF8] w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl h-[90vh] flex flex-col shadow-2xl overflow-hidden">
            
            <div className="w-10 h-1 bg-gray-300 rounded-full mx-auto mt-3 mb-1 sm:hidden shrink-0" />

            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between shrink-0 bg-white z-10 shadow-sm">
              <div>
                <h3 className="font-black text-xl text-gray-900">Tu Pedido</h3>
              </div>
              <button onClick={() => setCarritoAbierto(false)} className="p-2 rounded-full hover:bg-gray-100 text-gray-500 transition-colors">
                <X size={20} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto pb-20 scrollbar-hide">
              {/* Progreso Envío Gratis */}
              <div className="bg-orange-50 p-4 text-center border-b border-orange-100">
                <p className="text-xs font-bold text-orange-800">
                  {totalCarrito >= 15000 
                    ? '🎉 ¡Felicidades! Tienes envío gratis.' 
                    : `Agrega ${formatCLP(15000 - totalCarrito)} más para ENVÍO GRATIS`}
                </p>
                <div className="w-full bg-orange-200/50 rounded-full h-2 mt-2.5 overflow-hidden">
                  <div className="bg-[#E64A19] h-full rounded-full transition-all duration-500" style={{ width: `${Math.min(100, (totalCarrito / 15000) * 100)}%` }} />
                </div>
              </div>

              {/* Items */}
              <div className="bg-white p-5 space-y-4 shadow-sm border-b border-gray-100">
                {carrito.map(item => (
                  <div key={item.carritoId} className="flex items-center gap-3">
                    <div className="flex items-center gap-1 bg-gray-50 rounded-xl p-1 shrink-0 border border-gray-100">
                      <button onClick={() => restarItem(item.carritoId)}
                        className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-white hover:shadow-sm text-gray-600 transition-all">
                        <Minus size={14} />
                      </button>
                      <span className="w-6 text-center text-sm font-black">{item.cantidad}</span>
                      <button onClick={() => incrementarItem(item.carritoId)}
                        className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-[#D32F2F]/10 text-[#D32F2F] transition-all">
                        <Plus size={14} />
                      </button>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-gray-900 truncate">{item.nombre}</p>
                      <p className="text-xs text-[#D32F2F] font-semibold">{formatCLP(item.subtotal)}</p>
                    </div>
                    <button onClick={() => quitarItem(item.carritoId)}
                      className="p-2 text-gray-300 hover:text-red-500 transition-colors shrink-0">
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))}
              </div>

              {/* Cross-Selling */}
              {sugerenciasUpsell.length > 0 && (
                <div className="p-5 bg-gray-50/50 border-b border-gray-100">
                  <h4 className="font-black text-sm text-gray-800 mb-3 flex items-center gap-2">
                    <span>🔥</span> Completa tu pedido
                  </h4>
                  <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide">
                    {sugerenciasUpsell.map(item => (
                      <div key={item.id} className="bg-white rounded-2xl border border-gray-200 p-3 shadow-sm min-w-[160px] flex flex-col gap-2 shrink-0">
                        <div className="flex-1">
                          <p className="font-bold text-gray-900 text-xs line-clamp-2">{item.nombre}</p>
                          <p className="text-[#E64A19] font-black text-xs mt-1">{formatCLP(item.precioVenta)}</p>
                        </div>
                        <button
                          type="button"
                          onClick={() => agregarItem(item)}
                          className="w-full py-2 rounded-xl bg-gray-50 hover:bg-[#D32F2F] hover:text-white text-gray-700 text-xs font-bold transition-colors flex items-center justify-center gap-1 border border-gray-200 hover:border-[#D32F2F]"
                        >
                          <Plus size={14} /> Agregar
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Formulario One-Step Checkout */}
              <div className="p-5 space-y-5 bg-white">
                <h4 className="font-black text-lg text-gray-900">Datos de entrega</h4>

                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-gray-600 uppercase tracking-wider flex items-center gap-1.5">
                    <User size={12} /> Tu Nombre *
                  </label>
                  <input
                    className="w-full px-4 py-3 border-2 border-gray-100 rounded-2xl text-sm font-medium focus:outline-none focus:border-[#D32F2F] transition-colors bg-gray-50 focus:bg-white"
                    placeholder="¿Cómo te llamas?"
                    value={formPedido.nombre}
                    onChange={e => setFormPedido(p => ({ ...p, nombre: e.target.value }))}
                  />
                </div>

                <div className="space-y-1.5 bg-[#FFF4E5] border border-orange-200 p-4 rounded-2xl relative overflow-hidden">
                  <div className="absolute top-0 right-0 p-2 opacity-5 pointer-events-none">
                    <Gift size={64} />
                  </div>
                  <label className="text-xs font-black text-orange-900 flex items-center gap-1.5 relative z-10 uppercase tracking-wider">
                    <Phone size={14} className="text-[#E64A19]" /> Teléfono *
                    <span className="bg-[#E64A19] text-white text-[9px] px-2 py-0.5 rounded-full tracking-wide">+ PUNTOS</span>
                  </label>
                  <p className="text-[11px] text-orange-800/80 mb-2.5 relative z-10 font-medium">Te avisamos cuando tu pedido esté listo o si necesitamos confirmar algo. Además acumulas puntos.</p>
                  <input
                    className="w-full px-4 py-3 border-2 border-orange-200 rounded-xl text-sm font-bold focus:outline-none focus:border-[#E64A19] bg-white relative z-10"
                    placeholder="+56 9 1234 5678"
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel"
                    required
                    value={formPedido.telefono}
                    onChange={e => setFormPedido(p => ({ ...p, telefono: e.target.value }))}
                  />
                  {formPedido.telefono.trim() && !telValido && (
                    <p className="text-[11px] text-red-600 font-semibold relative z-10 mt-1">Revisa el número: debe tener al menos 8 dígitos.</p>
                  )}
                </div>

                <div className="space-y-2.5 pt-2">
                  <label className="text-xs font-bold text-gray-600 uppercase tracking-wider">¿Cómo lo quieres?</label>
                  {/* Sin "Mesa": en el local se pide por el QR de la mesa (CartaDigital) */}
                  <div className="grid grid-cols-2 gap-2.5">
                    {[
                      { id: 'llevar',   label: '🛍️',  nombre: 'Para llevar' },
                      { id: 'delivery', label: '🛵',  nombre: 'Delivery' },
                    ].map(t => (
                      <button key={t.id} type="button"
                        onClick={() => setFormPedido(p => ({ ...p, tipo: t.id }))}
                        className={`py-3 px-2 rounded-2xl border-2 text-center transition-all flex flex-col items-center justify-center gap-1
                          ${formPedido.tipo === t.id
                            ? 'border-[#D32F2F] bg-[#D32F2F]/5 shadow-sm'
                            : 'border-gray-100 hover:border-gray-200 bg-white'}`}>
                        <div className="text-xl">{t.label}</div>
                        <div className={`text-xs font-bold ${formPedido.tipo === t.id ? 'text-[#D32F2F]' : 'text-gray-600'}`}>{t.nombre}</div>
                      </button>
                    ))}
                  </div>
                </div>

                {formPedido.tipo === 'delivery' && (
                  <div className="space-y-4 animate-in fade-in slide-in-from-top-2">
                    <div className="space-y-1.5">
                      <label className="text-xs font-bold text-gray-600 uppercase tracking-wider flex items-center gap-1.5">
                        <MapPin size={12} /> Dirección de entrega
                      </label>
                      <input
                        className="w-full px-4 py-3 border-2 border-gray-100 rounded-2xl text-sm font-medium focus:outline-none focus:border-[#D32F2F] bg-gray-50 focus:bg-white"
                        placeholder="Calle, número, depto..."
                        value={formPedido.direccion}
                        onChange={e => setFormPedido(p => ({ ...p, direccion: e.target.value }))}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-xs font-bold text-gray-600 uppercase tracking-wider flex items-center gap-1.5">
                        <MapPin size={12} /> Ubicación exacta en el mapa
                      </label>
                      <p className="text-[11px] text-gray-500 font-medium">Toca el mapa para fijar tu ubicación y ayudar al repartidor.</p>
                      <div className="h-48 w-full rounded-2xl overflow-hidden border-2 border-gray-200 shadow-sm relative z-0">
                        <MapContainer center={mapCenter} zoom={15} style={{ height: '100%', width: '100%', zIndex: 0 }}>
                          <ChangeView center={mapCenter} zoom={15} />
                          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                          <MapSelector position={formPedido.ubicacion} setPosition={(pos) => setFormPedido(p => ({ ...p, ubicacion: pos }))} />
                        </MapContainer>
                      </div>
                    </div>
                  </div>
                )}

                {/* Programar para más tarde */}
                <div className="space-y-2.5 pt-2">
                  <button type="button"
                    onClick={() => setFormPedido(p => ({ ...p, esProgramado: !p.esProgramado }))}
                    className={`w-full flex items-center justify-between gap-3 p-4 rounded-2xl border-2 transition-all
                      ${formPedido.esProgramado ? 'border-indigo-400 bg-indigo-50' : 'border-gray-100 bg-white hover:border-gray-200'}`}>
                    <div className="flex items-center gap-3 text-left">
                      <CalendarClock size={20} className={formPedido.esProgramado ? 'text-indigo-600' : 'text-gray-400'} />
                      <div>
                        <p className={`text-sm font-bold ${formPedido.esProgramado ? 'text-indigo-900' : 'text-gray-700'}`}>
                          Programar para más tarde
                        </p>
                        <p className="text-[11px] text-gray-500">
                          {formPedido.tipo === 'delivery' ? 'Elige la hora de entrega' : 'Elige la hora de retiro'}
                        </p>
                      </div>
                    </div>
                    <div className={`w-11 h-6 rounded-full transition-colors relative shrink-0 ${formPedido.esProgramado ? 'bg-indigo-500' : 'bg-gray-200'}`}>
                      <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${formPedido.esProgramado ? 'translate-x-5' : ''}`} />
                    </div>
                  </button>

                  {formPedido.esProgramado && (
                    <div className="space-y-1.5 animate-in fade-in slide-in-from-top-2">
                      <label className="text-xs font-bold text-indigo-700 uppercase tracking-wider">
                        ¿Para cuándo lo dejamos listo?
                      </label>
                      <input
                        type="datetime-local"
                        min={aInputLocal(new Date(Date.now() + 15 * 60000))}
                        value={formPedido.fechaProgramada}
                        onChange={e => setFormPedido(p => ({ ...p, fechaProgramada: e.target.value }))}
                        className="w-full px-4 py-3 border-2 border-indigo-200 rounded-2xl text-sm font-bold focus:outline-none focus:border-indigo-500 bg-white"
                      />
                      {avisoHorario
                        ? <p className="text-xs text-orange-600 font-semibold">⚠️ {avisoHorario}</p>
                        : <p className="text-[11px] text-gray-500">Tu pedido entra a cocina a la hora indicada, no antes.</p>}
                    </div>
                  )}
                </div>

                <div className="space-y-1.5 pt-2">
                  <label className="text-xs font-bold text-gray-600 uppercase tracking-wider flex items-center gap-1.5">
                    <MessageSquare size={12} /> Notas para la cocina
                  </label>
                  <textarea
                    className="w-full px-4 py-3 border-2 border-gray-100 rounded-2xl text-sm font-medium focus:outline-none focus:border-[#D32F2F] bg-gray-50 focus:bg-white resize-none"
                    rows={2}
                    placeholder="Sin cebolla, extra salsa..."
                    value={formPedido.notas}
                    onChange={e => setFormPedido(p => ({ ...p, notas: e.target.value }))}
                  />
                </div>

                {/* Código promocional */}
                <div className="space-y-2 pt-4 border-t border-slate-100">
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
                    <Gift size={12} /> ¿Tienes un código promocional?
                  </label>
                  {promo ? (
                    <div className="flex items-center justify-between bg-green-50 border-2 border-green-200 rounded-2xl px-4 py-3">
                      <div>
                        <p className="text-sm font-black text-green-700 font-mono">{promo.codigo}</p>
                        <p className="text-xs text-green-600">
                          Descuento aplicado: −{formatCLP(descuentoPromo)}
                        </p>
                      </div>
                      <button type="button" onClick={quitarCodigo}
                        className="text-xs font-semibold text-green-700 hover:text-red-500 underline">
                        Quitar
                      </button>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <input
                        className="flex-1 px-4 py-3 border-2 border-gray-100 rounded-2xl text-sm font-bold font-mono uppercase focus:outline-none focus:border-[#D32F2F] bg-gray-50 focus:bg-white"
                        placeholder="EJ: BIENVENIDO10"
                        value={codigoInput}
                        onChange={e => { setCodigoInput(e.target.value.toUpperCase()); setPromoError(''); }}
                        onKeyDown={e => e.key === 'Enter' && aplicarCodigo()}
                      />
                      <button type="button" onClick={aplicarCodigo} disabled={validandoPromo || !codigoInput.trim()}
                        className="px-4 py-3 rounded-2xl bg-gray-900 hover:bg-black disabled:bg-gray-200 disabled:text-gray-400 text-white text-sm font-bold transition-colors shrink-0">
                        {validandoPromo ? '...' : 'Aplicar'}
                      </button>
                    </div>
                  )}
                  {promoError && <p className="text-xs text-red-500 font-medium">{promoError}</p>}
                </div>

                {/* Métodos de Pago */}
                <div className="space-y-3 pt-4 border-t border-slate-100">
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">¿Cómo quieres pagar?</label>
                  <div className="grid grid-cols-3 gap-2">
                    {METODOS_PAGO_WEB.map(mp => {
                      const activo = formPedido.metodoPago === mp.id;
                      return (
                        <button key={mp.id} type="button"
                          onClick={() => setFormPedido(p => ({ ...p, metodoPago: mp.id }))}
                          className={`relative px-2 py-4 rounded-2xl border text-center transition-all active:scale-95
                            ${activo
                              ? 'border-brand-500 bg-brand-50/60 shadow-sm shadow-brand-500/10'
                              : 'border-slate-200 bg-white hover:border-slate-300'}`}>
                          {activo && (
                            <span className="absolute top-2 right-2 w-4 h-4 rounded-full bg-brand-600 flex items-center justify-center">
                              <Check size={11} className="text-white" strokeWidth={3} />
                            </span>
                          )}
                          <mp.icon size={22} strokeWidth={1.8}
                            className={`mx-auto mb-2 ${activo ? 'text-brand-600' : 'text-slate-400'}`} />
                          <span className={`block text-xs font-bold ${activo ? 'text-brand-700' : 'text-slate-600'}`}>
                            {mp.nombre}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-xs text-slate-500 px-1 leading-relaxed">
                    {METODOS_PAGO_WEB.find(m => m.id === formPedido.metodoPago)?.desc}
                  </p>
                  {formPedido.metodoPago === 'transferencia' && (
                    <DatosTransferencia datos={paginaWeb.transferencia} monto={totalAPagar} />
                  )}
                </div>

              </div>
            </div>

            <div className="px-5 py-4 bg-white border-t border-slate-200 shrink-0 shadow-[0_-10px_20px_-10px_rgba(0,0,0,0.05)] z-10">
              {promo && (
                <div className="flex justify-between text-xs font-semibold text-slate-500 mb-2 px-1">
                  <span>Subtotal {formatCLP(totalCarrito)} · Descuento −{formatCLP(descuentoPromo)}</span>
                  <span className="text-green-600">Ahorras {formatCLP(descuentoPromo)}</span>
                </div>
              )}
              <button
                disabled={!formPedido.nombre.trim() || !telValido || enviando ||
                  (formPedido.esProgramado && !formPedido.fechaProgramada)}
                onClick={enviarPedido}
                className="w-full py-4 bg-brand-600 hover:bg-brand-700 disabled:bg-slate-200 disabled:text-slate-400
                  text-white font-black font-heading text-lg rounded-2xl transition-all active:scale-95 flex items-center justify-center gap-2 shadow-xl shadow-brand-500/20"
              >
                {enviando
                  ? <><div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Procesando...</>
                  : !formPedido.nombre.trim() || !telValido
                    ? 'Ingresa tu nombre y teléfono'
                  : formPedido.esProgramado && !formPedido.fechaProgramada
                    ? 'Elige fecha y hora'
                    : `${formPedido.esProgramado ? 'Programar' : 'Confirmar'} Pedido — ${formatCLP(totalAPagar)}`}
              </button>

              {/* Deber de información — Ley 21.719 sobre datos personales.
                  Debe estar a la vista donde se piden los datos, no escondido. */}
              <p className="text-[11px] leading-relaxed text-slate-400 text-center px-1">
                Usamos tu nombre y teléfono solo para preparar y entregar este pedido
                {formPedido.tipo === 'delivery' ? ', y tu dirección para el despacho' : ''}.
                No los compartimos con terceros ni te enviamos publicidad sin tu permiso.{' '}
                <Link to="/privacidad" className="underline hover:text-slate-600">
                  Cómo tratamos tus datos
                </Link>
              </p>
            </div>
          </div>
        </div>
      )}
      
      {/* Modal elige combo */}
      {comboEligiendo && (
        <ModalEligeCombo
          combo={comboEligiendo}
          onConfirmar={confirmarComboElige}
          onCerrar={() => setComboEligiendo(null)}
        />
      )}

      {/* Modal tamaño y agregados */}
      {itemOpciones && (
        <ModalOpciones
          item={itemOpciones}
          soloVer={soloVer}
          onConfirmar={confirmarOpciones}
          onCerrar={() => setItemOpciones(null)}
        />
      )}
    </div>
  );
}
